import React, { useEffect, useState, useCallback } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { fetchWeatherApi } from "openmeteo";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from "recharts";
import { useChartHover, formatTick } from "./chartHover";
import "./SensorDetail.css";
import InsighioSensorDetail from "./InsighioSensorDetail";
import FieldClimateGroupDetail from "./FieldClimateGroupDetail";
import { isWeatherCardStation } from "../Map/weatherStationCard";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_BASE_URL || "http://169.58.21.41/api";

const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
});

/** Derive template type from sensor name or type field */
const getTemplate = (sensor) => {
    const name = (sensor?.name || "").toUpperCase();
    const type = (sensor?.type || "").toUpperCase();
    if (name.startsWith("VWC") || type === "MOISTURE" || type === "SOILTEMPERATURE") return "vwc";
    if (name.startsWith("FLOW") || type === "RAINFALL") return "flow";
    if (name.startsWith("METEO") || name.startsWith("WEATHER") || type === "HUMIDITY" || type === "WINDSPEED" || type === "TEMPERATURE") return "weather";
    if (name.startsWith("EXO") || name.startsWith("WQ") || type === "MULTI") return "waterquality";
    return "vwc"; // default
};

const fmtNum = (v, dec = 1) => (v != null && !isNaN(v) ? Number(v).toFixed(dec) : "—");
const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return `${dt.getDate().toString().padStart(2,"0")}/${(dt.getMonth()+1).toString().padStart(2,"0")}/${dt.getFullYear()}`;
};
const fmtTime = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return `${dt.getHours().toString().padStart(2,"0")}:${dt.getMinutes().toString().padStart(2,"0")}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Chart with HTML overlay tooltip (Recharts-based, matches InsighioSensorDetail)
// ─────────────────────────────────────────────────────────────────────────────
const fmtX = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

const MiniChart = ({ data, color = "#4f46e5", label = "", unit = "" }) => {
    // data can be array of raw values or array of objects with _ts
    const chartData = data
        ? data.map((v, i) => typeof v === 'object' && v !== null ? v : { _ts: null, _val: v })
               .filter(r => {
                   const v = r._val ?? Object.values(r).find(x => typeof x === 'number' && !isNaN(x));
                   return v != null;
               })
        : [];

    const dataKey = chartData.length > 0 && chartData[0]._val !== undefined ? '_val' : null;

    const { wrapRef, tipRef, lineRef, chartProps } = useChartHover({
        length: chartData.length,
        renderTip: (idx) => {
            const point = chartData[idx];
            const value = point[dataKey];
            if (value == null || isNaN(value)) return null;
            return `
            <div class="chart-tip-time">${fmtX(point._ts)}</div>
            <div class="chart-tip-val" style="color:${color}">${Number(value).toFixed(dec)}<span class="chart-tip-unit">${unit || ''}</span></div>`;
        },
    });

    if (!chartData.length || dataKey === null) return (
        <div className="chart-card" style={{ minHeight: 110, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#94a3b8", fontSize: 12 }}>No data</span>
        </div>
    );

    const vals = chartData.map(d => d[dataKey]).filter(v => v != null && !isNaN(v));
    const mn  = Math.min(...vals);
    const mx  = Math.max(...vals);
    const dec = mx < 10 ? 3 : mx < 100 ? 2 : 1;
    const yMin = mn >= 0 && mn <= (mx - mn) / 2 ? 0 : 'auto';
    const yMax = mn === 0 && mx === 0 ? 1 : 'auto';
    const gradId = `sd-grad-${color.replace('#','')}`;

    return (
        <div className="chart-card" ref={wrapRef}>
            <div className="chart-card-title">{label}</div>
            <div ref={lineRef} className="chart-crosshair" />
            <div ref={tipRef}  className="chart-tip" style={{ display: 'none', position: 'absolute', pointerEvents: 'none', zIndex: 10 }} />
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 6, right: 4, left: 0, bottom: 0 }} {...chartProps}>
                    <defs>
                        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={color} stopOpacity={0.25} />
                            <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" />
                    <XAxis dataKey="_ts" hide />
                    <YAxis domain={[yMin, yMax]} width={44} tick={{ fontSize: 9, fill: '#94a3b8' }} tickCount={5}
                           tickFormatter={formatTick} />
                    <Tooltip content={() => null} cursor={false} isAnimationActive={false} />
                    <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.8}
                          fill={`url(#${gradId})`} dot={false} isAnimationActive={false} />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Stat card
// ─────────────────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, unit, sub, color = "#4f46e5" }) => (
    <div style={{
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
        padding: "14px 16px", display: "flex", flexDirection: "column", gap: 4,
        borderLeft: `4px solid ${color}`
    }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {label}
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", lineHeight: 1, fontFamily: "'IBM Plex Mono', monospace", fontVariantNumeric: "tabular-nums" }}>
            {value ?? "—"} <span style={{ fontSize: 13, fontWeight: 600, color: "#64748b", fontFamily: "inherit" }}>{unit}</span>
        </div>
        {sub && <div style={{ fontSize: 11, color: "#94a3b8" }}>{sub}</div>}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// History table (shared by all templates)
// ─────────────────────────────────────────────────────────────────────────────
const HistoryTable = ({ rows, columns, sensorName, onDownload }) => {
    const [page, setPage] = useState(1);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const PER_PAGE = 25;

    const filtered = rows.filter(r => {
        if (!startDate && !endDate) return true;
        const d = new Date(r._ts);
        if (startDate && d < new Date(startDate)) return false;
        if (endDate && d > new Date(endDate + "T23:59:59")) return false;
        return true;
    });

    const total = Math.ceil(filtered.length / PER_PAGE);
    const page_rows = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

    return (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, overflow: "hidden" }}>
            {/* Toolbar */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap" }}>
                <div style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>
                    {sensorName} — History ({filtered.length} records)
                </div>
                <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "5px 8px", fontSize: 12 }} />
                <span style={{ fontSize: 12, color: "#94a3b8" }}>→</span>
                <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "5px 8px", fontSize: 12 }} />
                <button onClick={() => onDownload(filtered)}
                    style={{ background: "#4f46e5", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                    CSV Export
                </button>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                    <thead>
                        <tr style={{ background: "#f8fafc" }}>
                            <th style={thStyle}>Date</th>
                            <th style={thStyle}>Time</th>
                            {columns.map(c => <th key={c.key} style={thStyle}>{c.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {page_rows.length === 0 ? (
                            <tr><td colSpan={2 + columns.length} style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>No data</td></tr>
                        ) : page_rows.map((r, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                                <td style={tdStyle}>{fmtDate(r._ts)}</td>
                                <td style={tdStyle}>{fmtTime(r._ts)}</td>
                                {columns.map(c => (
                                    <td key={c.key} style={{ ...tdStyle, fontWeight: 600, color: "#0f172a" }}>
                                        {r[c.key] != null ? `${fmtNum(r[c.key], c.dec ?? 2)} ${c.unit ?? ""}` : "—"}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {total > 1 && (
                <div style={{ display: "flex", justifyContent: "center", gap: 8, padding: "12px 20px", borderTop: "1px solid #f1f5f9" }}>
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={pageBtnStyle}>←</button>
                    <span style={{ fontSize: 12, color: "#64748b", alignSelf: "center" }}>{page} / {total}</span>
                    <button disabled={page === total} onClick={() => setPage(p => p + 1)} style={pageBtnStyle}>→</button>
                </div>
            )}
        </div>
    );
};

const thStyle = { padding: "9px 12px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle = { padding: "7px 12px", color: "#374151", whiteSpace: "nowrap" };
const pageBtnStyle = { background: "#f1f5f9", border: "none", borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 13, fontWeight: 700 };

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE: VWC — Soil Moisture / VWC_01–VWC_08
// Fields: toprakNem, toprakSicaklik, ec (stored in ph field)
// ─────────────────────────────────────────────────────────────────────────────
const VwcTemplate = ({ rows, meteo, sensorName, lat, lng }) => {
    const latest = rows[0] || {};
    const wKey = latest._ts ? `${fmtDate(latest._ts)}-${fmtTime(latest._ts).split(":")[0]}` : null;
    const w = wKey ? meteo[wKey] : null;

    const columns = [
        { key: "toprakNem", label: "Soil Moisture (%)", unit: "%", dec: 1 },
        { key: "toprakSicaklik", label: "Soil Temp (°C)", unit: "°C", dec: 1 },
        { key: "ph", label: "EC (dS/m)", unit: "dS/m", dec: 3 },
        { key: "meteoSoilMoisture", label: "Meteo Soil Moist.", unit: "%", dec: 1 },
        { key: "meteoSoilTemp", label: "Meteo Soil Temp (°C)", unit: "°C", dec: 1 },
    ];

    const enrichedRows = rows.map(r => ({
        ...r,
        meteoSoilMoisture: (() => {
            const key = `${fmtDate(r._ts)}-${fmtTime(r._ts).split(":")[0]}`;
            const mv = meteo[key]?.soilMoisture;
            return mv != null ? mv * 100 : null;
        })(),
        meteoSoilTemp: (() => {
            const key = `${fmtDate(r._ts)}-${fmtTime(r._ts).split(":")[0]}`;
            return meteo[key]?.soilTemp ?? null;
        })(),
    }));

    const downloadCSV = (filtered) => exportCSV(filtered, columns, sensorName);

    const mkData = (key) => rows.map(r => ({ _ts: r._ts, _val: r[key] })).filter(r => r._val != null);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Stat cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                <StatCard label="Soil Moisture" value={fmtNum(latest.toprakNem)} unit="%" color="#10b981"
                    sub={w ? `Meteo: ${fmtNum(w.soilMoisture * 100)}%` : undefined} />
                <StatCard label="Soil Temperature" value={fmtNum(latest.toprakSicaklik)} unit="°C" color="#f59e0b"
                    sub={w ? `Meteo: ${fmtNum(w.soilTemp)}°C` : undefined} />
                <StatCard label="EC" value={fmtNum(latest.ph, 3)} unit="dS/m" color="#6366f1" />
                {w?.precipitation > 0 && (
                    <StatCard label="Recent Rainfall" value={fmtNum(w.precipitation, 1)} unit="mm" color="#3b82f6" />
                )}
            </div>

            {/* Charts */}
            <div className="chart-grid">
                <MiniChart data={mkData('toprakNem')} color="#10b981" label="Soil Moisture (%)" unit="%" />
                <MiniChart data={mkData('toprakSicaklik')} color="#f59e0b" label="Soil Temperature (°C)" unit="°C" />
                <MiniChart data={mkData('ph')} color="#6366f1" label="EC (dS/m)" unit=" dS/m" />
                {meteo && Object.keys(meteo).length > 0 && (
                    <MiniChart
                        data={rows.map(r => {
                            const k = `${fmtDate(r._ts)}-${fmtTime(r._ts).split(":")[0]}`;
                            const v = meteo[k]?.soilMoisture;
                            return { _ts: r._ts, _val: v != null ? v * 100 : null };
                        }).filter(r => r._val != null)}
                        color="#06b6d4" label="Meteo Soil Moisture (%)" unit="%" />
                )}
            </div>

            {/* History */}
            <HistoryTable rows={enrichedRows} columns={columns} sensorName={sensorName} onDownload={downloadCSV} />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE: Flow — Water Flow / Flow_01–Flow_08
// Fields: havaSicaklik (flow rate L/s), havaNem (daily total L), ec (cumulative)
// ─────────────────────────────────────────────────────────────────────────────
const FlowTemplate = ({ rows, sensorName }) => {
    const latest = rows[0] || {};
    const columns = [
        { key: "havaSicaklik", label: "Flow Rate (L/s)", unit: "L/s", dec: 2 },
        { key: "havaNem", label: "Daily Volume (L)", unit: "L", dec: 0 },
        { key: "ph", label: "Cumulative (m³)", unit: "m³", dec: 3 },
    ];
    const downloadCSV = (filtered) => exportCSV(filtered, columns, sensorName);

    const mkData = (key) => rows.map(r => ({ _ts: r._ts, _val: r[key] })).filter(r => r._val != null);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                <StatCard label="Flow Rate" value={fmtNum(latest.havaSicaklik, 2)} unit="L/s" color="#3b82f6" />
                <StatCard label="Daily Volume" value={fmtNum(latest.havaNem, 0)} unit="L" color="#06b6d4" />
                <StatCard label="Cumulative" value={fmtNum(latest.ph, 3)} unit="m³" color="#8b5cf6" />
            </div>

            <div className="chart-grid">
                <MiniChart data={mkData('havaSicaklik')} color="#3b82f6" label="Flow Rate (L/s)" unit=" L/s" />
                <MiniChart data={mkData('havaNem')} color="#06b6d4" label="Daily Volume (L)" unit=" L" />
                <MiniChart data={mkData('ph')} color="#8b5cf6" label="Cumulative (m³)" unit=" m³" />
            </div>

            <HistoryTable rows={rows} columns={columns} sensorName={sensorName} onDownload={downloadCSV} />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE: Weather — Meteo-702c
// Fields: havaSicaklik (temp), havaNem (humidity), toprakSicaklik (wind), toprakNem (rain), ph (solar/pressure)
// ─────────────────────────────────────────────────────────────────────────────
const WeatherTemplate = ({ rows, meteo, sensorName }) => {
    const latest = rows[0] || {};
    const wKey = latest._ts ? `${fmtDate(latest._ts)}-${fmtTime(latest._ts).split(":")[0]}` : null;
    const w = wKey ? meteo[wKey] : null;

    const columns = [
        { key: "havaSicaklik", label: "Temperature (°C)", unit: "°C", dec: 1 },
        { key: "havaNem", label: "Humidity (%)", unit: "%", dec: 0 },
        { key: "toprakSicaklik", label: "Wind Speed (m/s)", unit: "m/s", dec: 1 },
        { key: "toprakNem", label: "Rainfall (mm)", unit: "mm", dec: 1 },
        { key: "ph", label: "Solar Radiation / Pressure", unit: "", dec: 1 },
    ];
    const downloadCSV = (filtered) => exportCSV(filtered, columns, sensorName);

    const mkData = (key) => rows.map(r => ({ _ts: r._ts, _val: r[key] })).filter(r => r._val != null);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                <StatCard label="Temperature" value={fmtNum(latest.havaSicaklik)} unit="°C" color="#ef4444"
                    sub={w ? `Meteo: ${fmtNum(w.temp)}°C` : undefined} />
                <StatCard label="Humidity" value={fmtNum(latest.havaNem, 0)} unit="%" color="#3b82f6"
                    sub={w ? `Meteo: ${fmtNum(w.humidity, 0)}%` : undefined} />
                <StatCard label="Wind Speed" value={fmtNum(latest.toprakSicaklik)} unit="m/s" color="#64748b" />
                <StatCard label="Rainfall" value={fmtNum(latest.toprakNem)} unit="mm" color="#6366f1"
                    sub={w ? `Meteo: ${fmtNum(w.precipitation, 1)}mm` : undefined} />
                <StatCard label="Solar / Pressure" value={fmtNum(latest.ph)} unit="" color="#f59e0b" />
            </div>

            <div className="chart-grid">
                <MiniChart data={mkData('havaSicaklik')} color="#ef4444" label="Temperature (°C)" unit="°C" />
                <MiniChart data={mkData('havaNem')} color="#3b82f6" label="Humidity (%)" unit="%" />
                <MiniChart data={mkData('toprakSicaklik')} color="#64748b" label="Wind Speed (m/s)" unit=" m/s" />
                <MiniChart data={mkData('toprakNem')} color="#6366f1" label="Rainfall (mm)" unit=" mm" />
                <MiniChart data={mkData('ph')} color="#f59e0b" label="Solar Radiation" unit="" />
            </div>

            <HistoryTable rows={rows} columns={columns} sensorName={sensorName} onDownload={downloadCSV} />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE: Water Quality — EXO-2b44
// Fields: ph (pH), ec (EC), dissolvedO2 (DO), turbidity, havaSicaklik (ORP), havaNem (Salinity)
// ─────────────────────────────────────────────────────────────────────────────
const WaterQualityTemplate = ({ rows, sensorName }) => {
    const latest = rows[0] || {};
    const columns = [
        { key: "pH",          label: "pH",             unit: "",      dec: 2 },
        { key: "Conductivity",label: "EC (µS/cm)",     unit: "µS/cm", dec: 1 },
        { key: "DO",          label: "DO (mg/L)",      unit: "mg/L",  dec: 2 },
        { key: "Turbitity",   label: "Turbidity (NTU)",unit: "NTU",   dec: 1 },
        { key: "ORP",         label: "ORP (mV)",       unit: "mV",    dec: 1 },
        { key: "Salinity",    label: "Salinity (ppt)", unit: "ppt",   dec: 2 },
        { key: "TDS",         label: "TDS (mg/L)",     unit: "mg/L",  dec: 1 },
        { key: "Temperature", label: "Water Temp (°C)","unit": "°C",  dec: 1 },
    ];
    const downloadCSV = (filtered) => exportCSV(filtered, columns, sensorName);

    const mkData = (key) => rows.map(r => ({ _ts: r._ts, _val: r[key] })).filter(r => r._val != null);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 12 }}>
                <StatCard label="pH"           value={fmtNum(latest.pH, 2)}           unit=""      color="#10b981" sub="Optimal: 6.5–8.5" />
                <StatCard label="EC"           value={fmtNum(latest.Conductivity, 1)} unit="µS/cm" color="#f59e0b" />
                <StatCard label="Dissolved O₂" value={fmtNum(latest.DO, 2)}           unit="mg/L"  color="#3b82f6" sub="Optimal: >6 mg/L" />
                <StatCard label="Turbidity"    value={fmtNum(latest.Turbitity, 1)}    unit="NTU"   color="#8b5cf6" />
                <StatCard label="ORP"          value={fmtNum(latest.ORP, 1)}          unit="mV"    color="#ef4444" />
                <StatCard label="Salinity"     value={fmtNum(latest.Salinity, 2)}     unit="ppt"   color="#06b6d4" />
                <StatCard label="TDS"          value={fmtNum(latest.TDS, 1)}          unit="mg/L"  color="#6366f1" />
                <StatCard label="Water Temp"   value={fmtNum(latest.Temperature, 1)}  unit="°C"    color="#a16207" />
            </div>

            <div className="chart-grid">
                <MiniChart data={mkData('pH')}          color="#10b981" label="pH"              unit="" />
                <MiniChart data={mkData('Conductivity')} color="#f59e0b" label="EC (µS/cm)"      unit=" µS/cm" />
                <MiniChart data={mkData('DO')}          color="#3b82f6" label="DO (mg/L)"        unit=" mg/L" />
                <MiniChart data={mkData('Turbitity')}   color="#8b5cf6" label="Turbidity (NTU)"  unit=" NTU" />
                <MiniChart data={mkData('ORP')}         color="#ef4444" label="ORP (mV)"         unit=" mV" />
                <MiniChart data={mkData('Salinity')}    color="#06b6d4" label="Salinity (ppt)"   unit=" ppt" />
                <MiniChart data={mkData('TDS')}         color="#6366f1" label="TDS (mg/L)"       unit=" mg/L" />
                <MiniChart data={mkData('Temperature')} color="#a16207" label="Water Temp (°C)"  unit="°C" />
            </div>

            <HistoryTable rows={rows} columns={columns} sensorName={sensorName} onDownload={downloadCSV} />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// CSV export helper
// ─────────────────────────────────────────────────────────────────────────────
const exportCSV = (rows, columns, sensorName) => {
    if (rows.length === 0) { alert("No data to export."); return; }
    const headers = ["Date", "Time", ...columns.map(c => c.label)];
    const lines = rows.map(r => [
        fmtDate(r._ts),
        fmtTime(r._ts),
        ...columns.map(c => r[c.key] != null ? fmtNum(r[c.key], c.dec ?? 2) : "")
    ].join(","));
    const csv = "﻿" + [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${sensorName}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
};

// ─────────────────────────────────────────────────────────────────────────────
// Fetch open-meteo historical weather
// ─────────────────────────────────────────────────────────────────────────────
const fetchMeteo = async (lat, lng, rows) => {
    try {
        if (!rows.length) return {};
        const dates = rows.map(r => new Date(r._ts)).filter(d => !isNaN(d));
        if (!dates.length) return {};
        const earliest = new Date(Math.min(...dates));
        const pastDays = Math.min(Math.ceil((Date.now() - earliest) / 86400000) + 1, 92);
        const responses = await fetchWeatherApi("https://api.open-meteo.com/v1/forecast", {
            latitude: lat, longitude: lng,
            hourly: ["temperature_2m", "relative_humidity_2m", "precipitation", "soil_temperature_6cm", "soil_moisture_3_to_9cm"],
            past_days: pastDays, timezone: "Europe/Istanbul"
        });
        const h = responses[0].hourly();
        const off = responses[0].utcOffsetSeconds();
        const len = (Number(h.timeEnd()) - Number(h.time())) / h.interval();
        const map = {};
        for (let i = 0; i < len; i++) {
            const dt = new Date((Number(h.time()) + i * h.interval() + off) * 1000);
            const key = `${fmtDate(dt.toISOString())}-${dt.getHours().toString().padStart(2,"0")}`;
            map[key] = {
                temp: h.variables(0).valuesArray()[i],
                humidity: h.variables(1).valuesArray()[i],
                precipitation: h.variables(2).valuesArray()[i],
                soilTemp: h.variables(3).valuesArray()[i],
                soilMoisture: h.variables(4).valuesArray()[i],
            };
        }
        return map;
    } catch { return {}; }
};

// ─────────────────────────────────────────────────────────────────────────────
// ROOT — SensorDetail
// ─────────────────────────────────────────────────────────────────────────────
export default function SensorDetail({ sensor, onClose }) {
    const { language } = useLanguage();
    const [rows, setRows] = useState([]);
    const [meteo, setMeteo] = useState({});
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshInterval, setRefreshInterval] = useState(() => {
        const saved = parseInt(localStorage.getItem('sensor_refresh_interval') || '0', 10);
        return isNaN(saved) ? 0 : saved;
    });

    const handleSetRefreshInterval = (val) => {
        setRefreshInterval(val);
        localStorage.setItem('sensor_refresh_interval', String(val));
    };

    const isInsighio = !!(sensor?.insighioDeviceId || sensor?.fieldclimateStationId);
    const id = sensor?.sensorId;
    const template = getTemplate(sensor);

    const defaultTo = new Date();
    const defaultFrom = new Date(defaultTo);
    defaultFrom.setDate(defaultFrom.getDate() - 7);
    const [dateFrom, setDateFrom] = useState(defaultFrom.toISOString().slice(0, 10));
    const [dateTo, setDateTo]   = useState(defaultTo.toISOString().slice(0, 10));

    const templateConfig = {
        vwc:          { label: "VWC — Soil Moisture", color: "#10b981" },
        flow:         { label: "Flow Meter", color: "#3b82f6" },
        weather:      { label: "Weather Station", color: "#f59e0b" },
        waterquality: { label: "Water Quality", color: "#6366f1" },
    }[template];

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        setError(null);

        const load = async () => {
            try {
                const [sRes, hRes] = await Promise.all([
                    fetch(`${API_URL}/sensors/${id}`, { headers: authHeaders() }),
                    fetch(`${API_URL}/sensors/${id}/history?limit=1000&from=${new Date(dateFrom).toISOString()}&to=${new Date(dateTo + 'T23:59:59').toISOString()}`, { headers: authHeaders() }),
                ]);

                if (!sRes.ok) throw new Error("Sensor not found");
                const sData = await sRes.json();
                setMeta(sData);

                let history = [];
                if (hRes.ok) {
                    const raw = await hRes.json();
                    // Strip device prefix from extra keys (e.g. "983dae692b44-Conductivity" → "Conductivity")
                    const stripPfx = k => k.replace(/^[0-9a-f]+-/i, '');
                    const flatExtra = (extra) => {
                        if (!extra || typeof extra !== 'object') return {};
                        const out = {};
                        for (const [k, v] of Object.entries(extra)) {
                            if (typeof v === 'number') out[stripPfx(k)] = v;
                        }
                        return out;
                    };
                    history = raw.map(r => {
                        const ex = flatExtra(r.extra);
                        return {
                            _ts: r.recordedAt,
                            // EXO water quality — read from extra first, fall back to standard cols
                            pH:          ex.pH          ?? r.ph          ?? null,
                            Conductivity:ex.Conductivity?? (r.ec > 0 ? r.ec : null),
                            DO:          ex.DO          ?? r.dissolvedO2 ?? null,
                            Turbitity:   ex.Turbitity   ?? r.turbidity   ?? null,
                            ORP:         ex.ORP         ?? null,
                            TDS:         ex.TDS         ?? null,
                            Salinity:    ex.Salinity    ?? null,
                            Temperature: ex.Temperature ?? r.temperature ?? null,
                            // METEO
                            'A.Temperature': ex['A.Temperature'] ?? r.temperature ?? null,
                            'A_Pressure':    ex['A_Pressure']    ?? null,
                            'R_Humidity':    ex['R_Humidity']    ?? r.humidity    ?? null,
                            'Precipitation': ex['Precipitation'] ?? r.rainfall    ?? null,
                            'S.Radiation':   ex['S.Radiation']   ?? null,
                            'V.Pressure':    ex['V.Pressure']    ?? null,
                            'W.Speed':       ex['W.Speed']       ?? r.windSpeed   ?? null,
                            'W.Direction':   ex['W.Direction']   ?? null,
                            // VWC
                            'VWC_15cm':            ex['VWC_15cm']            ?? null,
                            'VWC_50cm':            ex['VWC_50cm']            ?? null,
                            'S.Temperature_15cm':  ex['S.Temperature_15cm']  ?? r.soilTemp     ?? null,
                            'S.Temperature_50cm':  ex['S.Temperature_50cm']  ?? null,
                            'S.Conductivity_15cm': ex['S.Conductivity_15cm'] ?? null,
                            'S.Conductivity_50cm': ex['S.Conductivity_50cm'] ?? null,
                            // FLOW
                            Liter: ex.Liter ?? ex.Litre ?? null,
                            // legacy fields kept for non-insighio sensors
                            havaSicaklik: r.temperature,
                            havaNem: r.humidity,
                            toprakSicaklik: r.soilTemp,
                            toprakNem: r.soilMoisture,
                            ph: r.ph ?? r.ec ?? null,
                            ec: r.ec ?? null,
                            dissolvedO2: r.dissolvedO2 ?? null,
                            turbidity: r.turbidity ?? null,
                            source: r.source,
                        };
                    }).sort((a, b) => new Date(b._ts) - new Date(a._ts));
                }

                // Fallback: if no history, create a synthetic row from latestValue
                if (history.length === 0 && sData.latestValue != null) {
                    const row = { _ts: new Date().toISOString(), source: "live" };
                    const t = sData.type;
                    if (t === "Temperature")     row.havaSicaklik = sData.latestValue;
                    else if (t === "Humidity")   row.havaNem = sData.latestValue;
                    else if (t === "Moisture")   row.toprakNem = sData.latestValue;
                    else if (t === "SoilTemperature") row.toprakSicaklik = sData.latestValue;
                    history = [row];
                }

                setRows(history);

                // Fetch meteo only for templates that use it
                if ((template === "vwc" || template === "weather") && sData.lat && sData.lng && history.length > 0) {
                    const m = await fetchMeteo(sData.lat, sData.lng, history);
                    setMeteo(m);
                }
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [id, template, dateFrom, dateTo]);

    // ── FieldClimate station opened from one of its two map icons / list rows ──
    // Opened from anywhere without a group, it still gets the group view, never the old one.
    const fcSensor = sensor?.fcGroup ? sensor : isWeatherCardStation(sensor) ? { ...sensor, fcGroup: 'weather' } : null;
    if (fcSensor) return (
        <div className="sensor-detail-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="sensor-detail-scrollable">
                <div className="detail-content">
                    <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
                        <button onClick={onClose} className="back-btn-premium">
                            <span>←</span><span>{language === 'en' ? 'Back to Map' : 'Haritaya dön'}</span>
                        </button>
                    </div>
                    <FieldClimateGroupDetail sensor={fcSensor} language={language} />
                </div>
            </div>
        </div>
    );

    // ── insigh.io sensors: full chart modal ──
    if (isInsighio) return (
        <div className="sensor-detail-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="sensor-detail-scrollable">
                <div className="detail-content">
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
                        {/* Refresh interval seçici */}
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:12, fontWeight:600, color:'#6b7280' }}>Auto-refresh:</span>
                            {[{l:'Off',v:0},{l:'5 min',v:5},{l:'10 min',v:10},{l:'15 min',v:15},{l:'30 min',v:30},{l:'60 min',v:60}].map(opt => (
                                <button key={opt.v} onClick={() => handleSetRefreshInterval(opt.v)} style={{
                                    padding:'4px 12px', borderRadius:20, fontSize:11, fontWeight:700, cursor:'pointer',
                                    border: refreshInterval===opt.v ? '2px solid #6366f1' : '1px solid #e5e7eb',
                                    background: refreshInterval===opt.v ? '#6366f1' : '#fff',
                                    color: refreshInterval===opt.v ? '#fff' : '#6b7280',
                                    transition:'all 0.15s',
                                }}>{opt.l}</button>
                            ))}
                        </div>
                        <button onClick={onClose} className="back-btn-premium">
                            <span>←</span><span>Back to Map</span>
                        </button>
                    </div>
                    <InsighioSensorDetail sensor={sensor} language={language} refreshInterval={refreshInterval} />
                </div>
            </div>
        </div>
    );

    // ── Loading ──
    if (loading) return (
        <div className="sensor-detail-overlay">
            <div style={{ background: "#fff", borderRadius: 20, padding: "60px 40px", textAlign: "center" }}>
                <div style={{ width: 36, height: 36, border: "3px solid #e5e7eb", borderTopColor: "#6366f1", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
                <p style={{ color: "#64748b", fontWeight: 600 }}>Loading {sensor?.name}…</p>
            </div>
        </div>
    );

    // ── Error ──
    if (error) return (
        <div className="sensor-detail-overlay">
            <div style={{ background: "#fff", borderRadius: 20, padding: "60px 40px", textAlign: "center", maxWidth: 400 }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: "#ef4444", marginBottom: 12 }}>!</div>
                <h3 style={{ color: "#0f172a", marginBottom: 8 }}>Sensor not found</h3>
                <p style={{ color: "#64748b", marginBottom: 24 }}>{error}</p>
                <button onClick={onClose} style={{ background: "#4f46e5", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", cursor: "pointer", fontWeight: 700 }}>
                    Back
                </button>
            </div>
        </div>
    );

    const sensorName = meta?.name || sensor?.name || `Sensor #${id}`;
    const fieldName = sensor?.fieldName || "—";

    return (
        <div className="sensor-detail-overlay">
            <div className="sensor-detail-scrollable">
                <div className="detail-content">

                    {/* ── Header ── */}
                    <div className="detail-header-card" style={{ marginBottom: 24 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                            {/* Template icon */}
                            <div style={{
                                width: 52, height: 52, borderRadius: 16,
                                background: templateConfig.color, display: "flex",
                                alignItems: "center", justifyContent: "center",
                                flexShrink: 0,
                                boxShadow: `0 8px 20px -4px ${templateConfig.color}60`
                            }}>
                                <svg viewBox="0 0 24 24" width="24" height="24" fill="white" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                </svg>
                            </div>
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>
                                    Sensor Detail · {templateConfig.label}
                                </div>
                                <h1 style={{ margin: 0, fontSize: "1.6rem", fontWeight: 900, color: "#0f172a", letterSpacing: "-0.02em" }}>
                                    {sensorName}
                                </h1>
                                <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                                    <span style={{ fontSize: 12, color: "#64748b" }}>ID #{id}</span>
                                    {fieldName !== "—" && (
                                        <span style={{ background: "#f1f5f9", color: "#374151", fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 6 }}>
                                            {fieldName}
                                        </span>
                                    )}
                                    {meta?.lat && (
                                        <span style={{ fontSize: 11, color: "#94a3b8" }}>
                                            {Number(meta.lat).toFixed(4)}, {Number(meta.lng).toFixed(4)}
                                        </span>
                                    )}
                                    <span style={{ background: "#dcfce7", color: "#166534", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>
                                        {rows.length} records
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                            {/* Date range filter */}
                            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 10, padding: "6px 12px" }}>
                                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#6b7280" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                                <input
                                    type="date" value={dateFrom}
                                    onChange={e => setDateFrom(e.target.value)}
                                    style={{ border: "none", background: "transparent", fontSize: 12, fontWeight: 600, color: "#374151", outline: "none", cursor: "pointer" }}
                                />
                                <span style={{ fontSize: 11, color: "#94a3b8" }}>–</span>
                                <input
                                    type="date" value={dateTo}
                                    onChange={e => setDateTo(e.target.value)}
                                    style={{ border: "none", background: "transparent", fontSize: 12, fontWeight: 600, color: "#374151", outline: "none", cursor: "pointer" }}
                                />
                            </div>
                            {rows.length > 0 && (
                                <button
                                    onClick={() => exportCSV(rows, [], sensorName)}
                                    style={{ background: "#f1f5f9", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 10, padding: "9px 16px", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                                    Download CSV
                                </button>
                            )}
                            <button onClick={onClose} className="back-btn-premium">
                                <span>←</span>
                                <span>Back to Map</span>
                            </button>
                        </div>
                    </div>

                    {/* ── No data state ── */}
                    {rows.length === 0 ? (
                        <div style={{ background: "#fff", borderRadius: 16, padding: "80px 40px", textAlign: "center", border: "2px dashed #e5e7eb" }}>
                            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>
                            </div>
                            <h3 style={{ color: "#0f172a", marginBottom: 8 }}>No data yet</h3>
                            <p style={{ color: "#64748b" }}>Sensor <strong>{sensorName}</strong> has no recorded measurements.</p>
                        </div>
                    ) : (
                        <>
                            {/* ── Template-specific content ── */}
                            {template === "vwc" && (
                                <VwcTemplate rows={rows} meteo={meteo} sensorName={sensorName} lat={meta?.lat} lng={meta?.lng} />
                            )}
                            {template === "flow" && (
                                <FlowTemplate rows={rows} sensorName={sensorName} />
                            )}
                            {template === "weather" && (
                                <WeatherTemplate rows={rows} meteo={meteo} sensorName={sensorName} />
                            )}
                            {template === "waterquality" && (
                                <WaterQualityTemplate rows={rows} sensorName={sensorName} />
                            )}
                        </>
                    )}

                </div>
            </div>
        </div>
    );
}
